import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CreditCard, ExternalLink, Loader2, Download, ArrowUpCircle,
  AlertTriangle, CheckCircle2, RotateCcw, Users2, FolderKanban, Timer,
} from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

// Available plans mirrored from setup_stripe.py
const PLANS = [
  { key: "team_monthly",     name: "Team",     period: "monthly", amount: 12,  seats_hint: "per seat / month" },
  { key: "team_yearly",      name: "Team",     period: "yearly",  amount: 9.6, seats_hint: "per seat / month, billed yearly" },
  { key: "business_monthly", name: "Business", period: "monthly", amount: 24,  seats_hint: "per seat / month" },
  { key: "business_yearly",  name: "Business", period: "yearly",  amount: 19.2,seats_hint: "per seat / month, billed yearly" },
];

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() })
    .format((cents || 0) / 100);

const fmtDate = (unixOrIso) => {
  if (!unixOrIso) return "—";
  const d = typeof unixOrIso === "number" ? new Date(unixOrIso * 1000) : new Date(unixOrIso);
  return d.toLocaleDateString();
};

export default function Account() {
  const { activeWorkspace, user } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();

  const [billing, setBilling] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedKey, setSelectedKey] = useState("");
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [changing, setChanging] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = async () => {
    if (!activeWorkspace?.id) return;
    setLoading(true);
    try {
      const [b, u, m, inv] = await Promise.all([
        api.billingStatus().catch(() => null),
        api.billingUsage().catch(() => null),
        api.members(activeWorkspace.id).catch(() => []),
        api.billingInvoices().catch(() => ({ invoices: [] })),
      ]);
      setBilling(b);
      setUsage(u);
      setMembers(m || []);
      setInvoices(inv?.invoices || []);
      // Initialise plan selector from current sub
      const currentKey = b?.plan && b.plan !== "starter" ? `${b.plan}_${b.plan_period || "monthly"}` : "team_monthly";
      setSelectedKey(currentKey);
      setSelectedSeats(b?.seats || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspace?.id]);

  const myRole = members.find((mm) => mm.user_id === user?.id)?.role;
  const isOwner = myRole === "owner" || activeWorkspace?.is_platform_access;

  const currentPlanKey = useMemo(() => {
    if (!billing || billing.plan === "starter") return null;
    return `${billing.plan}_${billing.plan_period || "monthly"}`;
  }, [billing]);

  const planLabel = useMemo(() => {
    if (!billing || billing.plan === "starter") return "Starter (Free)";
    return `${billing.plan[0].toUpperCase() + billing.plan.slice(1)} · ${billing.plan_period || "monthly"}`;
  }, [billing]);

  if (!isOwner) {
    return (
      <div>
        <div className="text-sm text-[#6a736f]">Account</div>
        <h1 className="font-display text-4xl mt-1">Billing</h1>
        <div className="mt-8 bg-white rounded-xl border border-cream p-8 text-center" data-testid="account-owner-only">
          <AlertTriangle className="mx-auto text-coral" size={28} />
          <div className="mt-3 font-semibold text-lg">Owner-only area</div>
          <p className="text-sm text-[#3f4a44] max-w-md mx-auto mt-2">
            Only the workspace owner can manage the plan, view invoices, and cancel the subscription.
            Ask your workspace owner to make these changes.
          </p>
          <Link to="/app/settings" className="mt-6 inline-block btn-outline-forest px-4 py-2 rounded-md text-sm font-medium">
            Back to Settings
          </Link>
        </div>
      </div>
    );
  }

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { portal_url } = await api.billingPortal();
      window.location.href = portal_url;
    } catch (ex) {
      toast({ title: "Couldn't open billing portal", description: apiErrorMessage(ex, "") });
      setPortalBusy(false);
    }
  };

  const startCheckout = async () => {
    try {
      const plan = PLANS.find((p) => p.key === selectedKey) || PLANS[0];
      const { checkout_url } = await api.billingCheckout(plan.key, selectedSeats);
      window.location.href = checkout_url;
    } catch (ex) {
      toast({ title: "Couldn't start checkout", description: apiErrorMessage(ex, "") });
    }
  };

  const doChangePlan = async () => {
    setChanging(true);
    try {
      await api.billingChangePlan(selectedKey, selectedSeats);
      toast({ title: "Plan updated", description: "Your subscription was updated with prorated charges." });
      await load();
    } catch (ex) {
      toast({ title: "Couldn't change plan", description: apiErrorMessage(ex, "Try again.") });
    } finally {
      setChanging(false);
    }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      await api.billingCancel();
      toast({ title: "Cancellation scheduled", description: "You'll keep full access until the end of the current period." });
      setConfirmCancel(false);
      await load();
    } catch (ex) {
      toast({ title: "Couldn't cancel", description: apiErrorMessage(ex, "") });
    } finally {
      setCancelBusy(false);
    }
  };

  const doReactivate = async () => {
    setCancelBusy(true);
    try {
      await api.billingReactivate();
      toast({ title: "Subscription reactivated", description: "Your plan will keep renewing." });
      await load();
    } catch (ex) {
      toast({ title: "Couldn't reactivate", description: apiErrorMessage(ex, "") });
    } finally {
      setCancelBusy(false);
    }
  };

  const trialEndFmt = billing?.trial_end ? new Date(billing.trial_end).toLocaleDateString() : null;
  const periodEndFmt = billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : null;
  const hasSubscription = !!billing?.has_stripe_customer && billing?.plan !== "starter";
  const hasCancelPending = !!billing?.cancel_at_period_end;

  const seatsShortfall = usage && usage.seats_used > (billing?.seats || 1);

  return (
    <div data-testid="account-page">
      <div className="text-sm text-[#6a736f]">Account</div>
      <h1 className="font-display text-4xl mt-1">Billing & Plan</h1>
      <p className="text-sm text-[#3f4a44] mt-2 max-w-2xl">
        Change your plan, review your invoices, and cancel anytime. Everything is prorated to the day.
      </p>

      {/* Plan summary */}
      <div className="mt-6 bg-white rounded-xl border border-cream p-6" data-testid="plan-summary-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-forest text-white flex items-center justify-center">
              <CreditCard size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#6a736f]">Current plan</div>
              <div className="font-display text-2xl mt-1" data-testid="current-plan-label">{planLabel}</div>
              <div className="text-sm text-[#3f4a44] mt-2 space-y-0.5">
                {billing?.subscription_status && (
                  <div>
                    <span className="text-[#6a736f]">Status: </span>
                    <span className="font-medium capitalize" data-testid="subscription-status">
                      {billing.subscription_status.replace("_", " ")}
                    </span>
                  </div>
                )}
                {billing?.seats > 0 && (
                  <div><span className="text-[#6a736f]">Seats: </span><span className="font-medium">{billing.seats}</span></div>
                )}
                {billing?.subscription_status === "trialing" && trialEndFmt && (
                  <div className="text-coral">Trial ends {trialEndFmt}.</div>
                )}
                {periodEndFmt && billing?.subscription_status !== "trialing" && (
                  <div>
                    <span className="text-[#6a736f]">{hasCancelPending ? "Access until: " : "Next invoice: "}</span>
                    <span className="font-medium">{periodEndFmt}</span>
                  </div>
                )}
                {hasCancelPending && (
                  <div className="mt-2 inline-flex items-center gap-2 text-xs font-medium bg-coral/15 text-coral px-2 py-1 rounded" data-testid="cancel-pending-badge">
                    <AlertTriangle size={12} /> Cancels at period end
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {billing?.has_stripe_customer && (
              <button
                disabled={portalBusy}
                onClick={openPortal}
                data-testid="open-stripe-portal"
                className="btn-outline-forest inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
                title="Update card or billing details in Stripe"
              >
                <ExternalLink size={14} /> {portalBusy ? "Redirecting…" : "Card & tax details"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Usage */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="usage-summary">
        <UsageTile
          icon={Users2}
          label="Seats used"
          value={usage ? `${usage.seats_used} / ${usage.seats_purchased}` : "…"}
          hint={seatsShortfall ? "Over the seat limit — add seats" : "Active members in this workspace"}
          testid="usage-seats"
          alert={seatsShortfall}
        />
        <UsageTile
          icon={FolderKanban}
          label="Active projects"
          value={usage ? String(usage.active_projects) : "…"}
          hint="Projects tracked in this workspace"
          testid="usage-projects"
        />
        <UsageTile
          icon={Timer}
          label={`Hours in ${usage?.period_label || "this period"}`}
          value={usage ? usage.hours_this_period.toFixed(1) : "…"}
          hint="Sum of every tracked time entry"
          testid="usage-hours"
        />
      </div>

      {/* Change plan */}
      <div className="mt-8 bg-white rounded-xl border border-cream p-6" data-testid="change-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-semibold text-lg">{hasSubscription ? "Change plan or seats" : "Choose a plan"}</div>
            <p className="text-sm text-[#3f4a44] max-w-md mt-1">
              {hasSubscription
                ? "Upgrades take effect immediately and are prorated. Downgrades bill less on the next invoice."
                : "Start a 14-day free trial. Card required, cancel anytime before the trial ends."}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {PLANS.map((p) => {
            const isCurrent = currentPlanKey === p.key;
            const active = selectedKey === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setSelectedKey(p.key)}
                data-testid={`plan-option-${p.key}`}
                className={`text-left border rounded-lg p-4 transition ${
                  active ? "border-coral bg-coral/5 ring-1 ring-coral" : "border-cream hover:border-forest/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{p.name} · <span className="capitalize text-[#6a736f]">{p.period}</span></div>
                  {isCurrent && (
                    <span className="text-[10px] uppercase tracking-wide bg-forest text-white px-2 py-0.5 rounded">Current</span>
                  )}
                </div>
                <div className="mt-1 text-sm text-[#3f4a44]">
                  <span className="font-display text-xl">${p.amount}</span>
                  <span className="text-xs text-[#6a736f]"> {p.seats_hint}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex items-end flex-wrap gap-4">
          <div>
            <label className="text-xs font-semibold text-[#3f4a44]">Seats</label>
            <input
              type="number"
              min={1}
              max={500}
              value={selectedSeats}
              onChange={(e) => setSelectedSeats(Math.max(1, parseInt(e.target.value || "1", 10)))}
              data-testid="seat-count-input"
              className="mt-1 w-28 border border-cream rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[200px] text-xs text-[#6a736f]">
            {(() => {
              const p = PLANS.find((pp) => pp.key === selectedKey);
              const total = (p?.amount || 0) * selectedSeats * (p?.period === "yearly" ? 12 : 1);
              return (
                <>
                  New total: <span className="font-semibold text-forest">${total.toFixed(2)}</span>{" "}
                  {p?.period === "yearly" ? "billed yearly" : "per month"}
                </>
              );
            })()}
          </div>
          <div className="flex gap-2">
            {hasSubscription ? (
              <button
                disabled={changing || (currentPlanKey === selectedKey && (billing?.seats || 1) === selectedSeats)}
                onClick={doChangePlan}
                data-testid="apply-plan-change"
                className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowUpCircle size={14} /> {changing ? "Updating…" : "Apply plan change"}
              </button>
            ) : (
              <button
                onClick={startCheckout}
                data-testid="start-checkout"
                className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
              >
                Start 14-day trial
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="mt-8 bg-white rounded-xl border border-cream p-6" data-testid="invoices-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-lg">Invoices</div>
            <p className="text-sm text-[#3f4a44] mt-1">Every charge, receipt, and PDF from Stripe.</p>
          </div>
        </div>
        <div className="mt-5 overflow-x-auto">
          {loading ? (
            <div className="text-sm text-[#6a736f] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : invoices.length === 0 ? (
            <div className="text-sm text-[#6a736f] py-4" data-testid="invoices-empty">
              No invoices yet. Your first invoice will appear here once your trial ends.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#6a736f] border-b border-cream">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Invoice</th>
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-cream/60" data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-3 pr-4">{fmtDate(inv.created)}</td>
                    <td className="py-3 pr-4 font-mono text-xs">{inv.number || inv.id.slice(-8)}</td>
                    <td className="py-3 pr-4 text-xs">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        inv.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                        inv.status === "open" ? "bg-amber-100 text-amber-800" :
                        inv.status === "void" || inv.status === "uncollectible" ? "bg-red-100 text-red-800" :
                        "bg-cream text-[#3f4a44]"
                      }`}>{inv.status}</span>
                    </td>
                    <td className="py-3 pr-4 text-right font-medium">{money(inv.status === "paid" ? inv.amount_paid : inv.amount_due, inv.currency)}</td>
                    <td className="py-3 pr-4 text-right space-x-3">
                      {inv.invoice_pdf && (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`invoice-pdf-${inv.id}`}
                          className="text-forest hover:underline inline-flex items-center gap-1 text-xs"
                        >
                          <Download size={12} /> PDF
                        </a>
                      )}
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`invoice-view-${inv.id}`}
                          className="text-forest hover:underline inline-flex items-center gap-1 text-xs"
                        >
                          <ExternalLink size={12} /> View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Cancel / Reactivate */}
      {hasSubscription && (
        <div className="mt-8 border-t border-red-200 pt-8" data-testid="cancel-zone">
          <h2 className="font-display text-2xl text-red-700">
            {hasCancelPending ? "Reactivate subscription" : "Cancel subscription"}
          </h2>
          <div className="mt-4 bg-white rounded-xl border border-red-200 p-6 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              {hasCancelPending ? (
                <>
                  <div className="font-semibold text-forest">Cancellation is scheduled</div>
                  <p className="text-sm text-[#3f4a44] mt-1 max-w-md">
                    Your access continues until <b>{periodEndFmt}</b>. Reactivate to keep your plan running past that date.
                  </p>
                </>
              ) : (
                <>
                  <div className="font-semibold text-forest">Cancel at end of period</div>
                  <p className="text-sm text-[#3f4a44] mt-1 max-w-md">
                    You'll keep full access until <b>{periodEndFmt || "the end of the current period"}</b>.
                    No further charges after that. You can reactivate before then to stay subscribed.
                  </p>
                </>
              )}
            </div>
            {hasCancelPending ? (
              <button
                disabled={cancelBusy}
                onClick={doReactivate}
                data-testid="reactivate-subscription-btn"
                className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
              >
                <RotateCcw size={14} /> {cancelBusy ? "Reactivating…" : "Reactivate"}
              </button>
            ) : !confirmCancel ? (
              <button
                onClick={() => setConfirmCancel(true)}
                data-testid="open-cancel-subscription"
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Cancel subscription
              </button>
            ) : (
              <div className="w-full max-w-md">
                <div className="text-xs text-red-700 mb-2">
                  Are you sure? Your team will lose access after <b>{periodEndFmt}</b>.
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={cancelBusy}
                    onClick={doCancel}
                    data-testid="confirm-cancel-subscription"
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
                  >
                    {cancelBusy ? "Cancelling…" : "Yes, cancel at period end"}
                  </button>
                  <button
                    onClick={() => setConfirmCancel(false)}
                    className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Keep subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10 text-xs text-[#6a736f] flex items-center gap-2">
        <CheckCircle2 size={12} className="text-forest" />
        Powered by Stripe. Payments and PCI compliance handled outside Full Bench.
      </div>
    </div>
  );
}

function UsageTile({ icon: Icon, label, value, hint, testid, alert = false }) {
  return (
    <div className={`bg-white rounded-xl border p-4 ${alert ? "border-coral" : "border-cream"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#6a736f] uppercase tracking-wide">
        <Icon size={14} /> {label}
      </div>
      <div className={`font-display text-3xl mt-2 ${alert ? "text-coral" : "text-forest"}`}>{value}</div>
      <div className="text-xs text-[#6a736f] mt-1">{hint}</div>
    </div>
  );
}
